/**
 * FILE-OVERVIEW:
 * - 役割: スケジュール画面の統合コンテナ。サイドバー・本体・ダイアログを束ねる。
 * - 主な連携: CalendarMain.js, EventDialog.js, FilterSidebar.js, hooks/*
 * - 本番運用上の注意: 子コンポーネントへのprops構造は互換性を維持する。
 */

import React from 'react';
import { ThemeProvider, Box } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';

import CalendarMain from './CalendarMain';
import EventDialog from './EventDialog';
import MobileNavigation from './MobileNavigation';
import FilterSidebar from './FilterSidebar';
import LoadingSpinner from '../Common/LoadingSpinner';
import ErrorMessage from '../Common/ErrorMessage';

import { useAuth } from '../../hooks/useAuth';
import { useCalendarState } from '../../hooks/useCalendarState';
import { useCalendarEvents } from '../../hooks/useCalendarEvents';

import { theme } from '../../utils/colors';
import type { AuthUser } from '../../hooks/useAuth';

interface FullCalendarComponentProps {
  /** App が合成したログインユーザー。無い場合は useAuth の結果を使う */
  user?: AuthUser | null;
  isDesk?: boolean | null;
  isReadOnly?: boolean | null;
  onSignOut?: () => void;
  /** モバイル時のサイドバー開閉を親が持つ場合に渡される */
  mobileDrawerOpen?: boolean;
  /** App の useState セッターをそのまま受け取るため更新関数も許容する */
  setMobileDrawerOpen?: React.Dispatch<React.SetStateAction<boolean>>;
}

const FullCalendarComponent = (props: FullCalendarComponentProps) => {
  const {
    user: parentUser = null,
    isDesk: parentIsDesk = null,
    isReadOnly: parentIsReadOnly = null,
    onSignOut: parentSignOut = () => {},
    mobileDrawerOpen,
    setMobileDrawerOpen,
  } = props;
  // レスポンシブ判定: モバイル/タブレット(～1023px) / PC(1024px～)
  const isMobile = useMediaQuery('(max-width:1023px)');

  // 認証情報は parentUser があれば優先利用し、無い場合のみ useAuth で取得する。
  // useAuth の読み込みでカレンダー描画をブロックしないことで初期表示の体感速度を改善する。
  const { currentUser, isLoading: isAuthLoading, displayName } = useAuth();
  const calendarState = useCalendarState(currentUser);
  const calendarEvents = useCalendarEvents(currentUser, calendarState.autoUpdate);
  const isDrawerControlled = typeof setMobileDrawerOpen === 'function';

  const setDrawerOpen = React.useCallback<React.Dispatch<React.SetStateAction<boolean>>>((open) => {
    if (isDrawerControlled) {
      setMobileDrawerOpen?.(open);
      return;
    }
    calendarState.setIsDrawerOpen(open);
  }, [calendarState, isDrawerControlled, setMobileDrawerOpen]);

  const effectiveCalendarState = React.useMemo(() => {
    const controlledOpen = typeof mobileDrawerOpen === 'boolean' ? mobileDrawerOpen : false;
    const effectiveOpen = isDrawerControlled ? controlledOpen : calendarState.isDrawerOpen;

    return {
      ...calendarState,
      isDrawerOpen: effectiveOpen,
      setIsDrawerOpen: setDrawerOpen,
      toggleDrawer: (open: boolean) => () => setDrawerOpen(open),
    };
  }, [calendarState, isDrawerControlled, mobileDrawerOpen, setDrawerOpen]);

  // Authenticator 配下でレンダリングされるため、parentUser が存在すれば認証済みとみなす。
  // 内部の認証取得失敗時のみエラー表示にフォールバックする。
  const isAuthenticatedFromParent = Boolean(parentUser);
  if (!isAuthenticatedFromParent) {
    if (isAuthLoading) return <LoadingSpinner />;
    if (!currentUser || !currentUser.isAuthenticated) return <ErrorMessage message="ログインしてください" />;
  }

  // 親/内部どちらかで Desks 判定が取れればデスク扱いにする
  const effectiveIsDesk = Boolean(parentIsDesk || currentUser?.isDesk || (currentUser?.groups || []).includes('Desks'));
  const effectiveIsReadOnly = Boolean(
    parentIsReadOnly
    || currentUser?.isReadOnly
    || (currentUser?.groups || []).some((g) => String(g || '').toLowerCase() === 'read_only')
  );
  const effectiveUser = parentUser ?? currentUser;
  const effectiveSignOut = parentSignOut;

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          maxWidth: 1920,
          width: '100%',
          height: '100%',
          minHeight: 0,
          overflow: 'hidden',
          mx: 'auto',
          bgcolor: 'background.paper',
          boxShadow: 3,
        }}
      >
        <CalendarMain
          isMobile={isMobile}
          calendarState={effectiveCalendarState}
          calendarEvents={calendarEvents}
          currentUser={effectiveUser}
          isReadOnly={effectiveIsReadOnly}
        />

        <EventDialog
          calendarState={effectiveCalendarState}
          calendarEvents={calendarEvents}
          isDesk={effectiveIsDesk}
          isReadOnly={effectiveIsReadOnly}
          // 親からの user（parentUser）があれば優先して渡す
          currentUser={parentUser ?? currentUser}
          displayName={displayName}
        />

        {isMobile && <MobileNavigation calendarState={effectiveCalendarState} />}

        <FilterSidebar
          isMobile={isMobile}
          calendarState={effectiveCalendarState}
          user={effectiveUser}
          isDesk={effectiveIsDesk}
          onSignOut={effectiveSignOut}
        />
      </Box>
    </ThemeProvider>
  );
};

export default FullCalendarComponent;
